import { useEffect, useState } from "react";

import ScaleMakeAsyncSelect from "../../../../components/common/ScaleMakeAsyncSelect";

import ScaleModelAsyncSelect from "../../../../components/common/ScaleModelAsyncSelect";

type Props = {
  open: boolean;
  scale: any;
  customerId: string;

  onClose: () => void;

  onSave: (id: string, data: any, customerId: string) => Promise<void>;
};

export default function UpdateScaleModal({
  open,
  scale,
  customerId,
  onClose,
  onSave,
}: Props) {
  const [form, setForm] = useState({
    identifier: "",

    makeId: "",
    make: "",

    modelId: "",
    model: "",

    capacity: "",
    capacityUnit: "kg",
    division: "",
    divisionUnit: "kg",

    serialNumber: "",
    location: "",
    comment: "",
  });

  useEffect(() => {
    if (!scale) return;

    let capacity = "";
    let capacityUnit = "kg";
    let division = "";
    let divisionUnit = "kg";

    const match = scale.capacity?.match(
      /([\d.]+)\s*([a-zA-Z]+)\s*[×x]\s*([\d.]+)\s*([a-zA-Z]+)/,
    );

    if (match) {
      capacity = match[1];
      capacityUnit = match[2];
      division = match[3];
      divisionUnit = match[4];
    }

    setForm({
      identifier: scale.identifier ?? "",
      makeId: scale.makeId ?? "",
      make: scale.makeName ?? "",

      modelId: scale.modelId ?? "",
      model: scale.modelName ?? "",
      capacity,
      capacityUnit,
      division,
      divisionUnit,
      serialNumber: scale.serialNumber ?? "",
      location: scale.location ?? "",
      comment: scale.comment ?? "",
    });
  }, [scale]);

  if (!open || !scale) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-bold">Update Scale</h2>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="font-medium">Id</label>

            <input
              className="border rounded w-full p-2 mt-1"
              value={form.identifier}
              onChange={(e) =>
                setForm({
                  ...form,
                  identifier: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="font-medium">Make</label>

            <ScaleMakeAsyncSelect
              value={
                form.make
                  ? {
                      value: form.makeId || form.make,
                      label: form.make,
                      __isNew__: !form.makeId,
                    }
                  : null
              }
              onChange={(selected: any) => {
                setForm((prev) => ({
                  ...prev,

                  makeId: selected?.__isNew__ ? "" : selected?.value,
                  make: selected?.label ?? "",

                  // Clear model when make changes
                  modelId: "",
                  model: "",
                }));
              }}
            />
          </div>

          <div>
            <label className="font-medium">Model</label>

            <ScaleModelAsyncSelect
              value={
                form.model
                  ? {
                      value: form.modelId || form.model,
                      label: form.model,
                      __isNew__: !form.modelId,
                    }
                  : null
              }
              onChange={(selected: any) => {
                if (!selected) {
                  setForm((prev) => ({
                    ...prev,
                    modelId: "",
                    model: "",
                  }));
                  return;
                }

                const modelCapacity = selected.capacities?.[0];

                const capacity = modelCapacity?.capacity?.toString() ?? "";

                const division = modelCapacity?.division?.toString() ?? "";

                const capacityUnit = modelCapacity?.capacityUnitName ?? "kg";

                const divisionUnit =
                  modelCapacity?.divisionUnitName ?? capacityUnit;

                setForm((prev) => ({
                  ...prev,

                  modelId: selected.__isNew__ ? "" : selected.value,
                  model: selected.label,

                  capacity,
                  division,
                  capacityUnit,
                  divisionUnit,
                }));
              }}
            />
          </div>

          <div className="md:col-span-2">
            <label className="font-medium mb-2 block">Capacity</label>

            <div className="grid grid-cols-4 gap-3">
              <input
                type="number"
                className="border rounded p-2"
                placeholder="Capacity"
                value={form.capacity}
                onChange={(e) =>
                  setForm({
                    ...form,
                    capacity: e.target.value,
                  })
                }
              />

              <select
                className="border rounded p-2"
                value={form.capacityUnit}
                onChange={(e) =>
                  setForm({
                    ...form,
                    capacityUnit: e.target.value,
                  })
                }
              >
                <option>kg</option>
                <option>g</option>
                <option>t</option>
                <option>lb</option>
                <option>oz</option>
              </select>

              <input
                type="number"
                className="border rounded p-2"
                placeholder="Division"
                value={form.division}
                onChange={(e) =>
                  setForm({
                    ...form,
                    division: e.target.value,
                  })
                }
              />

              <select
                className="border rounded p-2"
                value={form.divisionUnit}
                onChange={(e) =>
                  setForm({
                    ...form,
                    divisionUnit: e.target.value,
                  })
                }
              >
                <option>kg</option>
                <option>g</option>
                <option>t</option>
                <option>lb</option>
                <option>oz</option>
              </select>
            </div>
          </div>

          <div>
            <label className="font-medium">Serial Number</label>

            <input
              className="border rounded w-full p-2 mt-1"
              value={form.serialNumber}
              onChange={(e) =>
                setForm({
                  ...form,
                  serialNumber: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="font-medium">Location</label>

            <input
              className="border rounded w-full p-2 mt-1"
              value={form.location}
              onChange={(e) =>
                setForm({
                  ...form,
                  location: e.target.value,
                })
              }
            />
          </div>

          <div className="md:col-span-2">
            <label className="font-medium">Comment</label>

            <textarea
              rows={5}
              className="border rounded w-full p-2 mt-1"
              value={form.comment}
              onChange={(e) =>
                setForm({
                  ...form,
                  comment: e.target.value,
                })
              }
            />
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2 border rounded-lg">
            Cancel
          </button>

          <button
            onClick={async () => {
              await onSave(
                scale.id,
                {
                  ...form,
                  capacity: `${form.capacity} ${form.capacityUnit} × ${form.division} ${form.divisionUnit}`,
                },
                customerId,
              );

              onClose();
            }}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
}
